import axios from 'axios';
import { BASE_URL, ENDPOINTS } from './config';

// Wrapper function for admin login
export const loginAdmin = async (email, password) => {
  try {
    const response = await axios.post(`${BASE_URL}${ENDPOINTS.loginAdmin}`, {
      email,
      password,
    });

    return response.data; // Return response data (token, etc.)
  } catch (error) {
    console.error('Error during admin login:', error.response?.data || error.message);
    throw error.response?.data || error;
  }
};

const makePrivateRequest = {
  async get(url, config) {
    return await this.request("get", url, config);
  },

  async post(url, data, config) {
    return await this.request("post", url, data, config);
  },

  async put(url, data, config) {
    return await this.request("put", url, data, config);
  },

  async patch(url, data, config) {
    return await this.request("patch", url, data, config);
  },

  async delete(url, config) {
    return await this.request("delete", url, config);
  },

  async request(method, url, data = null, config = {}) {
    const token = localStorage.getItem("token");
    try {

      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
      }

      const response = await axios({
        method,
        url,
        data,
        ...config,
      });

      return response.data;
    } catch (error) {
      // Handle errors, e.g., redirect to login page for unauthorized access
      const status = error?.response?.status;

      console.error("Error in making request", status, error?.response?.data?.err, token);
      if (status === 400 && error.response.data.err === 'Invalid token') {
        localStorage.removeItem("token");
        window.location.href = '/login';
      }

      console.error("Error:", error);
      throw error;
    }
  },
};

export const getAllCourses = async () => {
  try {
    const response = await axios.get(`${BASE_URL}${ENDPOINTS.getAllCourse}`);
    return response.data; // Assuming your API returns data in the body
  } catch (error) {
    console.error("Error fetching courses:", error);
    throw error; // Rethrow error for handling in the calling function
  }
};

export const getFullCourseContentForAdmin = async (courseId) => {
  try {
    const response = await makePrivateRequest.get(`${BASE_URL}${ENDPOINTS.getFullCourseContentForAdmin}/${courseId}`);
    return response; // Assuming your API returns data in the body
  } catch (error) {
    console.error("Error fetching full course content for admin:", error);
    throw error; // Rethrow error for handling in the calling function
  }
};

export const getCourseById = async (id) => {
  try {
    const response = await axios.get(`${BASE_URL}${ENDPOINTS.getCourseById}/${id}`);
    return response.data; // Assuming your API returns data in the body
  } catch (error) {
    console.error("Error fetching course by Id:", error);
    throw error; // Rethrow error for handling in the calling function
  }
};


export const updateCourseById = async (id, data) => {
  try {
    const response = await axios.patch(`${BASE_URL}${ENDPOINTS.updateCourseById}/${id}`, data);
    return response.data; // Assuming your API returns data in the body
  } catch (error) {
    console.error("Error updating course by Id:", error);
    throw error; // Rethrow error for handling in the calling function
  }
};


export const getUnitDetails = async (courseId, unitId) => {
  try {
    const response = await axios.get(`${BASE_URL}${ENDPOINTS.getUnitDetails}/${courseId}/${unitId}`);
    return response.data; // Assuming your API returns data in the body
  } catch (error) {
    console.error("Error updating course by Id:", error);
    throw error; // Rethrow error for handling in the calling function
  }
};

export const createLevelForAdmin = async (levelData) => {
  try {
    const response = await makePrivateRequest.post(
      `${BASE_URL}${ENDPOINTS.createLevel}`,
      levelData, 
    );
    return response;
  } catch (error) {
    console.error("Error creating level:", error);
    throw error;
  }
};

export const updateLevelById = async (id, data) => {
  try {
    const response = await axios.put(`${BASE_URL}${ENDPOINTS.updateLevel}/${id}`, {levelData: data});
    return response.data; 
  } catch (error) {
    console.error("Error updating course by Id:", error);
    return error; 
  }
};

export const archiveLevelById = async (levelId) => {
  try {
    const response = await axios.patch(`${BASE_URL}${ENDPOINTS.archiveLevel}/${levelId}`);
    return response.data; 
  } catch (error) {
    console.error("Error archiving level by Id:", error);
    return error; 
  }
};

export const restoreArchivedLevelById = async (levelId, payload = {}) => {
  try {
    const response = await axios.post(
      `${BASE_URL}${ENDPOINTS.restoreArchivedLevelById}/${levelId}`,
      payload,
    );
    return response.data;
  } catch (error) {
    console.error("Error unarchiving level by Id:", error);
    return error;
  }
};

export const createQuestionsForLevel = async (questions, options = {}) => {
  if (!Array.isArray(questions) || questions.length === 0) {
    return { success: true, message: "No questions to create." };
  }

  const { notificationType, notificationMessage } = options;

  try {
    const formData = new FormData();

    const serializedQuestions = questions.map((questionEntry, questionIndex) => {
      const { payload, attachments } = questionEntry ?? {};
      const attachmentMeta = {};

      if (attachments?.questionAudioFile) {
        const fieldName = `questionAudio_${questionIndex}`;
        formData.append(fieldName, attachments.questionAudioFile);
        attachmentMeta.questionAudioField = fieldName;
      }

      if (attachments?.hintAudioFile) {
        const fieldName = `hintAudio_${questionIndex}`;
        formData.append(fieldName, attachments.hintAudioFile);
        attachmentMeta.hintAudioField = fieldName;
      }

      if (Array.isArray(attachments?.optionAudioFiles) && attachments.optionAudioFiles.length) {
        const optionAudioMeta = [];
        attachments.optionAudioFiles.forEach((optionAttachment, optionIndex) => {
          if (!optionAttachment?.file) {
            return;
          }
          const fieldName = `optionAudio_${questionIndex}_${optionIndex}`;
          formData.append(fieldName, optionAttachment.file);
          optionAudioMeta.push({ text: optionAttachment.text, fieldName });
        });
        if (optionAudioMeta.length) {
          attachmentMeta.optionAudioFields = optionAudioMeta;
        }
      }

      return {
        ...payload,
        attachmentMeta,
      };
    });

    formData.append("questions", JSON.stringify(serializedQuestions));

    if (notificationType) {
      formData.append("notificationType", notificationType);
    }
    if (notificationMessage) {
      formData.append("notificationMessage", notificationMessage);
    }

    const response = await makePrivateRequest.post(
      `${BASE_URL}${ENDPOINTS.createQuestionsForLevel}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response;
  } catch (error) {
    console.error("Error creating questions for level in bulk:", error);
    return {
      success: false,
      error: error?.response?.data || error,
    };
  }
};

export const archiveQuestionsForLevel = async (levelId, questionIds) => {
  try {
    const response = await makePrivateRequest.post(
      `${BASE_URL}${ENDPOINTS.archiveLevelQuestions}`,
      { levelId, questionIds }
    );
    return response;
  } catch (error) {
    console.error("Error archiving questions for level:", error);
    throw error?.response?.data || error;
  }
};

export const restoreQuestionsForLevel = async (levelId, questionIds) => {
  try {
    const response = await makePrivateRequest.post(
      `${BASE_URL}${ENDPOINTS.restoreLevelQuestions}`,
      { levelId, questionIds }
    );
    return response;
  } catch (error) {
    console.error("Error restoring questions for level:", error);
    throw error?.response?.data || error;
  }
};

export const reorderQuestionsForLevel = async (reorderedQuestion) => {
  try {
    const response = await makePrivateRequest.patch(
      `${BASE_URL}${ENDPOINTS.reorderLevelQuestions}`,
      reorderedQuestion
    );
    return response;
  } catch (error) {
    console.error("Error restoring questions for level:", error);
    throw error?.response?.data || error;
  }
};

export const createUnitWithLevelsAndQuestions  = async (fullPayload) => {
  const isFormData = typeof FormData !== "undefined" && fullPayload instanceof FormData;

  if (!isFormData) {
    console.error("createUnitWithLevelsAndQuestions expects FormData built by caller");
    return { success: false, message: "FormData is required" };
  }

  try {
    const response = await makePrivateRequest.post(
      `${BASE_URL}${ENDPOINTS.createUnitWithLevelsAndQuestions}`,
      fullPayload,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response;
  } catch (error) {
    console.error("Error creating unit:", error);
    return error;
  }
}

export const createCourseDraft  = async (fullPayload, creatorInfo = {}) => {
  try {
    const response = await makePrivateRequest.post(`${BASE_URL}${ENDPOINTS.createCourseDraft}`, { fullPayload, ...creatorInfo });
    return response;
  } catch (error) {
    console.error("Error creating course draft:", error);
    return error; 
  }
}

export const getAllUviForAdmin  = async (courseId , courseName) => {
  try {
    const response = await makePrivateRequest.get(`${BASE_URL}${ENDPOINTS.getAllUviForAdmin}?courseId=${courseId}&courseName=${courseName}`);
    return response.data; 
  } catch (error) {
    console.error("Error :", error);
    return error; 
  }
};

export const getAIReviewedUVIQuestions  = async (courseId , courseName) => {
  try {
    console.log("courseId", courseId);  
    console.log("courseName", courseName);  
    const response = await makePrivateRequest.get(`${BASE_URL}${ENDPOINTS.getAIReviewedUVIQuestions}?courseId=${courseId}&courseName=${courseName}`);
    return response.data; 
  } catch (error) {
    console.error("Error :", error);
    return error; 
  }
};

export const getAllUpgradedLARForAdmin  = async (courseId , courseName) => {
  try {
    const response = await makePrivateRequest.get(`${BASE_URL}${ENDPOINTS.getAllUpgradedLARForAdmin}?courseId=${courseId}&courseName=${courseName}`);
    return response.data; 
  } catch (error) {
    console.error("Error :", error);
    return error; 
  }
};

export const reviewQuestion  = async (enrolledQuestionId,  expertAction, expertRemark) => {
  try {
    const response = await makePrivateRequest.post(`${BASE_URL}${ENDPOINTS.reviewQuestion}`, {enrolledQuestionId, expertRemark, expertAction});
    return response; 
  } catch (error) {
    console.error("Error :", error);
    return error; 
  }
};


export const uploadExcelForCourse = async(data) => {
  try {
    const response = await makePrivateRequest.post(`${BASE_URL}${ENDPOINTS.uploadExcelForCourse}`, data);
    return response; 
  } catch (error) {
    console.error("Error :", error);
    return error; 
  }
}


export const linkAudioForCourse = async(data) => {
  try {
    const response = await makePrivateRequest.post(`${BASE_URL}${ENDPOINTS.linkAudioForCourse}`, data);
    return response; 
  } catch (error) {
    console.error("Error :", error);
    return error; 
  }
}

export const updateAudioFileForQuestion = async(data) => {
  try {
    const response = await makePrivateRequest.post(`${BASE_URL}${ENDPOINTS.updateAudioFileForQuestion}`, data, {
      headers: {
        "Content-Type": "multipart/form-data", // Required for file uploads
      }
    });
    return response; 
  } catch (error) {
    console.error("Error :", error);
    return error; 
  }
}

export const uploadFlag = async(data) => {
  try {
    const response = await makePrivateRequest.post(`${BASE_URL}${ENDPOINTS.uploadFlag}`, data, {
      headers: {
        "Content-Type": "multipart/form-data", // Required for file uploads
      }
    });
    return response; 
  } catch (error) {
    console.error("Error :", error);
    return error; 
  }
}

export const markCoursePublish = async(data) => {
  try {
    const response = await makePrivateRequest.post(`${BASE_URL}${ENDPOINTS.markCoursePublish}`, data);
    return response; 
  } catch (error) {
    console.error("Error :", error);
    return error; 
  }
}


export const updateCourseStatus = async(data) => {
  try {
    const response = await makePrivateRequest.post(`${BASE_URL}${ENDPOINTS.updateCourseStatus}`, data);
    return response; 
  } catch (error) {
    console.error("Error :", error);
    return error; 
  }
}

export const getUserSubscriptionForAdmin  = async (userId) => {
  try {
    const response = await makePrivateRequest.post(`${BASE_URL}${ENDPOINTS.getUserSubscriptionForAdmin}`, {userId});
    return response; 
  } catch (error) {
    console.error("Error :", error);
    return error; 
  }
};
export const deleteCourse = async(data) => {
  try {
    const response = await makePrivateRequest.post(`${BASE_URL}${ENDPOINTS.deleteCourse}`, data);
    return response; 
  } catch (error) {
    console.error("Error :", error);
    return error; 
  }
}
export const setTargetAccent = async(data) => {
  try {
    const response = await makePrivateRequest.post(`${BASE_URL}${ENDPOINTS.setTargetAccent}`, data);
    return response; 
  } catch (error) {
    console.error("Error :", error);
    return error; 
  }
}

export const getAiModels  = async () => {
  try {
    const response = await makePrivateRequest.get(`${BASE_URL}${ENDPOINTS.getAiModels}`);
    return response; 
  } catch (error) {
    console.error("Error :", error);
    return error; 
  }
};

export const getAllReviewVoiceNotesForAdmin  = async () => {
  try {
    const response = await makePrivateRequest.get(`${BASE_URL}${ENDPOINTS.getAllReviewVoiceNotesForAdmin}`);
    return response.data; 
  } catch (error) {
    console.error("Error fetch all review voice notes for admin:", error);
    return error; 
  }
};

export const getAllReviewUpgradedLARForAdmin  = async () => {
  try {
    const response = await makePrivateRequest.get(`${BASE_URL}${ENDPOINTS.getAllReviewUpgradedLARForAdmin}`);
    return response.data; 
  } catch (error) {
    console.error("Error fetch all review upgraded LAR for admin:", error);
    return error; 
  }
};

export const updateExpertReviewsForAIFeedback = async (payload) => {
  try {
    const response = await makePrivateRequest.patch(`${BASE_URL}${ENDPOINTS.updateExpertReviewsForAIFeedback}`, payload);
    return response;
  } catch (error) {
    console.error("Error updating expert reviews for AI feedback:", error);
    return error;
  }
};

export const createTipOfDay = async (tipData) => {
  try {
    const formData = new FormData();
    formData.append("title", tipData.title);
    formData.append("description", tipData.description);
    formData.append("type", tipData.type);
    formData.append("reward", tipData.reward);
    // No publishDate — a new tip always joins the back of the rotation queue automatically.
    if (tipData.type === "audio" && tipData.audioFile) {
      formData.append("file", tipData.audioFile);
    }

    const response = await makePrivateRequest.post(
      `${BASE_URL}${ENDPOINTS.createTipOfDay}`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );

    return response;
  } catch (error) {
    console.error("Error creating tip of the day:", error);
    return {
      success: false,
      error: error?.response?.data || error,
    };
  }
};

export const getAllTipsOfDayForAdmin = async ({ month, year, type } = {}) => {
  try {
    const params = new URLSearchParams();
    if (month) params.set("month", month);
    if (year) params.set("year", year);
    if (type && type !== "all") params.set("type", type);
    const queryString = params.toString() ? `?${params.toString()}` : "";

    const response = await makePrivateRequest.get(`${BASE_URL}${ENDPOINTS.getAllTipsOfDayForAdmin}${queryString}`);
    return response;
  } catch (error) {
    console.error("Error fetching tips of the day:", error);
    return {
      success: false,
      error: error?.response?.data || error,
    };
  }
};

export const getLiveTipOfDay = async () => {
  try {
    const response = await makePrivateRequest.get(`${BASE_URL}${ENDPOINTS.getLiveTipOfDay}`);
    return response;
  } catch (error) {
    console.error("Error fetching live tip of the day:", error);
    return {
      success: false,
      error: error?.response?.data || error,
    };
  }
};

export const reorderTipsOfDay = async (reorderedTips) => {
  try {
    const response = await makePrivateRequest.patch(`${BASE_URL}${ENDPOINTS.reorderTipsOfDay}`, reorderedTips);
    return response;
  } catch (error) {
    console.error("Error reordering tips of the day:", error);
    return {
      success: false,
      error: error?.response?.data || error,
    };
  }
};

export const getTipOfDayActive = async () => {
  try {
    const response = await makePrivateRequest.get(`${BASE_URL}${ENDPOINTS.tipOfDayActive}`);
    return response;
  } catch (error) {
    console.error("Error fetching Tip of the Day status:", error);
    return {
      success: false,
      error: error?.response?.data || error,
    };
  }
};

export const setTipOfDayActive = async (enabled) => {
  try {
    const response = await makePrivateRequest.patch(`${BASE_URL}${ENDPOINTS.tipOfDayActive}`, { enabled });
    return response;
  } catch (error) {
    console.error("Error updating Tip of the Day status:", error);
    return {
      success: false,
      error: error?.response?.data || error,
    };
  }
};

export const updateTipOfDay = async (id, tipData) => {
  try {
    const formData = new FormData();
    formData.append("title", tipData.title);
    formData.append("description", tipData.description);
    formData.append("type", tipData.type);
    formData.append("reward", tipData.reward);
    // No publishDate — a tip's date only ever changes via rotation or a manual reorder.
    if (tipData.type === "audio" && tipData.audioFile) {
      formData.append("file", tipData.audioFile);
    }

    const response = await makePrivateRequest.patch(
      `${BASE_URL}${ENDPOINTS.updateTipOfDay}/${id}`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );

    return response;
  } catch (error) {
    console.error("Error updating tip of the day:", error);
    return {
      success: false,
      error: error?.response?.data || error,
    };
  }
};

export const deleteTipOfDay = async (id) => {
  try {
    const response = await makePrivateRequest.delete(`${BASE_URL}${ENDPOINTS.deleteTipOfDay}/${id}`);
    return response;
  } catch (error) {
    console.error("Error deleting tip of the day:", error);
    return {
      success: false,
      error: error?.response?.data || error,
    };
  }
};

export const getCommentsForAdminOfDay = async (tipId) => {
  try {
    const response = await makePrivateRequest.get(`${BASE_URL}${ENDPOINTS.tipOfDayComments}/${tipId}/comments/admin`);
    return response;
  } catch (error) {
    console.error("Error fetching comments for tip of the day:", error);
    return {
      success: false,
      error: error?.response?.data || error,
    };
  }
};

export const replyToCommentOfDay = async (tipId, commentId, text) => {
  try {
    const response = await makePrivateRequest.post(
      `${BASE_URL}${ENDPOINTS.tipOfDayComments}/${tipId}/comments/${commentId}/reply`,
      { text }
    );
    return response;
  } catch (error) {
    console.error("Error replying to comment:", error);
    return {
      success: false,
      error: error?.response?.data || error,
    };
  }
};

export const updateReplyOfDay = async (commentId, text) => {
  try {
    const response = await makePrivateRequest.patch(
      `${BASE_URL}${ENDPOINTS.tipOfDayComments}/comments/${commentId}`,
      { text }
    );
    return response;
  } catch (error) {
    console.error("Error updating reply:", error);
    return {
      success: false,
      error: error?.response?.data || error,
    };
  }
};

export const deleteCommentOfDay = async (commentId) => {
  try {
    const response = await makePrivateRequest.delete(`${BASE_URL}${ENDPOINTS.tipOfDayComments}/comments/${commentId}`);
    return response;
  } catch (error) {
    console.error("Error deleting comment:", error);
    return {
      success: false,
      error: error?.response?.data || error,
    };
  }
};